 

import { CUSTOMER } from 'Component/App/App.config';
import { SORT_DIRECTION_TYPE } from 'Route/CategoryPage/CategoryPage.config';
import { BEST_MATCH_SORT_OPTION_VALUE, NONE_SORT_OPTION_VALUE } from 'Route/SearchPage/SearchPage.config';
import BrowserDatabase from 'Util/BrowserDatabase';
import { Field, Fragment } from 'Util/Query';
import {
    canonicalUrl, hrefLang, metaRobots
} from 'Util/Seo/QueryData';
/** @namespace Bodypwa/Query/ProductList/Query */
export class ProductListQuery {

    options = {};

    getQuery(options) {
        if (!options) {
            throw new Error('Missing argument `options`');
        }

        this.options = options;

        return this._getProductsField();
    }

    getProductsSmallImageByUrlKeysQuery(urlKeys) {
        if (!Array.isArray(urlKeys) || !urlKeys.length) {
            throw new Error('Missing or invalid argument `urlKeys`');
        }

        return new Field('products')
            .addArgument('filter', 'ProductAttributeFilterInput', {
                url_key: { in: urlKeys }
            })
            .addField(
                new Field('items')
                    .addField('url_key')
                    .addField(this._getProductSmallField())
            );
    }

    getProductsImagesBySkusQuery(skus) {
        if (!skus || !Array.isArray(skus) || !skus.length) {
            throw new Error('Missing or invalid argument `skus`');
        }

        // Build products field
        const products = new Field('products')
            .addArgument('filter', 'ProductAttributeFilterInput', {
                sku: { in: skus }
            })
            .addField(
                new Field('items')
                    .addField('sku')
                    .addField(this._getProductImageField()) // image
                    .addField(this._getProductThumbnailField()) // thumbnail
                    .addField(this._getProductSmallField()) // small_image
            );

        return products;
    }

    _getProductsField() {
        const products = new Field('products')
            .addFieldList(this._getProductFields());

        this._getProductArguments().forEach((arg) => products.addArgument(...arg));

        return products;
    }

    _getPriceFilter(key, value) {
        const [from, to] = value[0].split('_');

        if (from === '*') {
            return { [key]: { to } };
        }

        if (to === '*') {
            return { [key]: { from } };
        }

        return {
            [key]: { from, to }
        };
    }

    _getCustomFilters = (filters = {}) => (
        Object.entries(filters).reduce((acc, [key, attribute]) => {
            if (!attribute.length) {
                return acc;
            }

            if (key === 'price') {
                return {
                    ...acc,
                    ...this._getPriceFilter(key, attribute)
                };
            }

            return {
                ...acc,
                [key]: { in: attribute }
            };
        }, {})
    );

    _getFilterArgumentMap() {
        return {
            categoryIds: (id) => ({ category_id: { eq: id } }),
            categoryUrlPath: (url) => ({ category_url_path: { eq: url } }),
            priceRange: ({ min, max }) => {
                const price = {};

                if (min) {
                    price.from = min;
                }

                if (max) {
                    price.to = max;
                }

                return { price };
            },
            productsSkuArray: (sku) => ({ sku: { in: sku } }),
            productSKU: (sku) => ({ sku: { eq: sku } }),
            productID: (id) => ({ id: { eq: id } }),
            productUrlPath: (url) => ({ url_key: { eq: url } }),
            customFilters: this._getCustomFilters,
            newToDate: (date) => ({ news_to_date: { gteq: date } }),
            conditions: (conditions) => ({ conditions: { eq: conditions } }),
            customerGroupId: (id) => ({ customer_group_id: { eq: id } })
        };
    }

    _getProductAltField() {
        return new Field('alt_image').addFieldList(this._getProductThumbnailFields());
    }

    _getProductSwatchesImagesField() {
        return new Field('alt_image').addFieldList(this._getProductThumbnailFields());
    }

    _getItemsField() {
        const items = new Field('items')
            .addFieldList(this._getProductInterfaceFields());

        items.addField(this._getProductAltField())
            .addField(hrefLang())
            .addField(canonicalUrl())
            .addField(metaRobots());

        return items;
    }

    _getProductField() {
        const { isForLinkedProducts } = this.options;

        return new Field('product')
            .addFieldList(this._getProductInterfaceFields(true, isForLinkedProducts));
    }

    _getShortDescriptionFields() {
        return [
            'html'
        ];
    }

    _getShortDescriptionField() {
        return new Field('short_description')
            .addFieldList(this._getShortDescriptionFields());
    }

    _getStockItemField() {
        return new Field('stock_item')
            .addFieldList(this._getStockItemFields());
    }

    _getStockItemFields() {
        return [
            'in_stock',
            'min_sale_qty',
            'max_sale_qty',
            'qty_increments'
        ];
    }

    _getBreadcrumbFields() {
        return [
            'category_id',
            'category_name',
            'category_level',
            'category_url',
            'category_is_active'
        ];
    }

    _getBreadcrumbsField() {
        return new Field('breadcrumbs')
            .addFieldList(this._getBreadcrumbFields());
    }

    _getCategoryFields() {
        return [
            'id',
            'name',
            'url',
            this._getBreadcrumbsField()
        ];
    }

    _getCategoriesField() {
        return new Field('categories')
            .addFieldList(this._getCategoryFields());
    }

    _getMinimalPriceFields() {
        return [
            this._getDiscountField(),
            this._getFinalPriceField(),
            this._getFinalPriceExclTaxField(),
            this._getRegularPriceField(),
            this._getRegularPriceExclTaxField(),
            this._getDefaultPriceField(),
            this._getDefaultFinalPriceField(),
            this._getDefaultFinalPriceExclTaxField()
        ];
    }

    _getMinimalPriceField() {
        return new Field('minimum_price')
            .addFieldList(this._getMinimalPriceFields());
    }

    _getMaximalPriceField() {
        return new Field('maximum_price')
            .addFieldList(this._getMinimalPriceFields());
    }

    _getPriceRangeFields() {
        // Using an array as potentially would want to add maximum price
        return [
            this._getMinimalPriceField(),
            this._getMaximalPriceField()
        ];
    }

    getPriceRangeField() {
        return new Field('price_range')
            .addFieldList(this._getPriceRangeFields());
    }

    _getVariantsField() {
        const { isPlp = false, notRequireInfo = false, isWidget = false } = this.options;

        // For PLP page we have optimized variants graphql field
        const variantsField = isPlp || notRequireInfo || isWidget ? 'variants_plp' : 'variants';

        return new Field(variantsField)
            .setAlias('variants')
            .addFieldList(this._getVariantFields());
    }

    _getConfigurableProductFragmentFields() {
        const { isPlp = false, notRequireInfo = false, isWidget = false } = this.options;

        if (isPlp || notRequireInfo || isWidget) {
            return [
                this._getConfigurableOptionsField()
            ];
        }

        return [
            this._getConfigurableOptionsField(),
            this._getPlacementSuggestionsField()
        ];
    }

     _getPlacementSuggestionsField() {
        return new Field('placement_suggestions')
            .addFieldList(this._getPlacementSuggestionsFields());
    }

    _getPlacementSuggestionsFields() {
        return [
            new Field('trigger_attribute_code'),
            new Field('rules').addFieldList([
                new Field('trigger_value_index'),
                new Field('trigger_label'),
                new Field('preselections').addFieldList([
                    new Field('attribute_code'),
                    new Field('values').addFieldList([
                        new Field('value_index'),
                        new Field('is_default'),
                        new Field('is_default_no_message')
                    ])
                ])
            ])
        ];
    }

    _getCustomizableTextValueFields() {
        return [
            'price',
            'priceInclTax',
            'priceExclTax',
            'price_type',
            'currency',
            'sku',
            'max_characters'
        ];
    }

    _getCustomizableTextValueField(alias) {
        return new Field('value')
            .addFieldList(this._getCustomizableTextValueFields())
            .setAlias(alias);
    }

    _getCustomizableTextFields(alias) {
        return [
            this._getCustomizableTextValueField(alias),
            'product_sku'
        ];
    }

    _getCustomizableFileValueField(alias) {
        return new Field('value')
            .addFieldList([
                'price',
                'priceInclTax',
                'priceExclTax',
                'price_type',
                'currency',
                'sku',
                'file_extension'
            ])
            .setAlias(alias);
    }

    _getCustomizableAreaOption() {
        return new Fragment('CustomizableAreaOption')
            .addFieldList(this._getCustomizableTextFields('areaValues'));
    }

    _getCustomizableFieldOption() {
        return new Fragment('CustomizableFieldOption')
            .addFieldList(this._getCustomizableTextFields('fieldValues'));
    }

    _getCustomizableFileOption() {
        return new Fragment('CustomizableFileOption')
            .addFieldList([this._getCustomizableFileValueField('fileValues')]);
    }

    _getCustomizableDateValueFields() {
        return [
            'price',
            'priceInclTax',
            'priceExclTax',
            'price_type',
            'currency',
            'sku'
        ];
    }

    _getCustomizableDateValueField() {
        return new Field('value')
            .addFieldList(this._getCustomizableDateValueFields());
    }

    _getCustomizableDateFields(alias) {
        return [
            this._getCustomizableDateValueField(alias),
            'product_sku'
        ];
    }

    _getCustomizableDateOption() {
        return new Fragment('CustomizableDateOption')
            .addFieldList(this._getCustomizableDateFields());
    }

    _getCustomizableSelectionValueFields() {
        return [
            'uid',
            'option_type_id',
            'price',
            'priceInclTax',
            'priceExclTax',
            'price_type',
            'currency',
            'sku',
            'title',
            'sort_order'
        ];
    }

    _getCustomizableSelectionValueField(alias) {
        return new Field('value')
            .addFieldList(this._getCustomizableSelectionValueFields())
            .setAlias(alias);
    }

    _getCustomizableCheckboxOption() {
        return new Fragment('CustomizableCheckboxOption')
            .addFieldList([this._getCustomizableSelectionValueField('checkboxValues')]);
    }

    _getCustomizableMultiOption() {
        return new Fragment('CustomizableMultipleOption')
            .addFieldList([this._getCustomizableSelectionValueField('checkboxValues')]); // same as checkbox
    }

    _getCustomizableDropdownOption() {
        return new Fragment('CustomizableDropDownOption')
            .addFieldList([this._getCustomizableSelectionValueField('dropdownValues')]);
    }

    _getCustomizableRadioOption() {
        return new Fragment('CustomizableRadioOption')
            .addFieldList([this._getCustomizableSelectionValueField('dropdownValues')]); // same as dropdown
    }

    _getCustomizableProductFragmentOptionsFields() {
        return [
            this._getCustomizableDropdownOption(),
            this._getCustomizableRadioOption(),
            this._getCustomizableCheckboxOption(),
            this._getCustomizableMultiOption(),
            this._getCustomizableFieldOption(),
            this._getCustomizableAreaOption(),
            this._getCustomizableFileOption(),
            this._getCustomizableDateOption(),
            'title',
            'required',
            'sort_order',
            'type',
            'uid'
        ];
    }

    _getCustomizableProductFragmentOptionsField() {
        return new Field('options')
            .addFieldList(this._getCustomizableProductFragmentOptionsFields());
    }

    _getCustomizableProductFragment() {
        return new Fragment('CustomizableProductInterface')
            .addFieldList([this._getCustomizableProductFragmentOptionsField()]);
    }

    _getSimpleProductFragmentFields() {
        return [
            this._getTierPricesField()
        ];
    }

    _getVirtualProductFragmentFields() {
        return [
            this._getTierPricesField()
        ];
    }

    _getTierPricesField() {
        return new Field('price_tiers')
            .addFieldList(this._getTierPricesFields());
    }

    _getTierPricesFields() {
        return [
            this._getDiscountField(),
            this._getFinalPriceField(),
            'quantity'
        ];
    }

    _getDiscountField() {
        return new Field('discount')
            .addField('amount_off')
            .addField('percent_off');
    }

    _getFinalPriceField() {
        return new Field('final_price')
            .addField('currency')
            .addField('value');
    }

    _getFinalPriceExclTaxField() {
        return new Field('final_price_excl_tax')
            .addField('currency')
            .addField('value');
    }

    _getRegularPriceField() {
        return new Field('regular_price')
            .addField('currency')
            .addField('value');
    }

    _getRegularPriceExclTaxField() {
        return new Field('regular_price_excl_tax')
            .addField('currency')
            .addField('value');
    }

    _getDefaultFinalPriceExclTaxField() {
        return new Field('default_final_price_excl_tax')
            .addField('currency')
            .addField('value');
    }

    _getDefaultPriceField() {
        return new Field('default_price')
            .addField('currency')
            .addField('value');
    }

    _getDefaultFinalPriceField() {
        return new Field('default_final_price')
            .addField('currency')
            .addField('value');
    }

    _getBundleProductFragment() {
        return new Fragment('BundleProduct')
            .addFieldList(this._getBundleProductFragmentFields());
    }

    _getConfigurableProductFragment() {
        return new Fragment('ConfigurableProduct')
            .addFieldList(this._getConfigurableProductFragmentFields());
    }

    _getSimpleProductFragment() {
        return new Fragment('SimpleProduct')
            .addFieldList(this._getSimpleProductFragmentFields());
    }

    _getVirtualProductFragment() {
        return new Fragment('VirtualProduct')
            .addFieldList(this._getVirtualProductFragmentFields());
    }

    _getSortOptionFields() {
        return [
            'value',
            'label'
        ];
    }

    _getSortOptionsField() {
        return new Field('options')
            .addFieldList(this._getSortOptionFields());
    }

    _getSortFields() {
        return [
            this._getSortOptionsField()
        ];
    }

    _getSortField() {
        return new Field('sort_fields')
            .addFieldList(this._getSortFields());
    }

    _getSwatchDataFields() {
        return [
            'type',
            'value'
        ];
    }

    _getSwatchDataField() {
        return new Field('swatch_data')
            .addFieldList(this._getSwatchDataFields());
    }

    _getAggregationsField() {
        return new Field('aggregations')
            .setAlias('filters')
            .addFieldList(this._getAggregationsFields());
    }

    _getProductFields() {
        const { requireInfo, isSingleProduct, notRequireInfo } = this.options;

        // do not request total count for PDP
        if (isSingleProduct || notRequireInfo) {
            return [
                this._getItemsField(isSingleProduct)
            ];
        }

        // for filters only request
        if (requireInfo) {
            return [
                this._getSortField(),
                this._getAggregationsField()
            ];
        }

        return [
            'total_count',
            this._getItemsField(),
            this._getPageInfoField()
        ];
    }

    _getProductInterfaceFields(isVariant, isForLinkedProducts = false) {
        const {
            isPlp = false,
            isWidget = false,
            isSingleProduct,
            noAttributes = false,
            noVariants = false,
            noVariantAttributes = false,
            notRequireInfo = false
        } = this.options;

        // Basic fields returned always
        const fields = [
            'uid',
            'id',
            'sku',
            'name',
            'brand_name',
            'meta_title',
            'type_id',
            'jewelry_type',
            'customs_code',
            'campaign_label',
            'flash_label',
            'product_label_usp',
            this._getQualitySymbolsField(),
            new Field('discontinued_on_store_front').setAlias('discontinued')
        ];

        if (!isPlp && !notRequireInfo && !isWidget) {
            fields.push(
                'stock_status',
                'salable_qty',
                this._getStockItemField(),
                this._getProductImageField(),
                this._getProductThumbnailField(),
                this._getShortDescriptionField(),
                'special_from_date',
                'special_to_date'
            );
        } else {
            fields.push('stock_status');
        }

        // Additional fields, which we want to return always, except when it's variants on PLP (due to hugh number of items)
        if (!(isPlp && isVariant && notRequireInfo && isWidget)) {
            fields.push(
                this._getProductSmallField()
            );
        }

        // if it is normal product and we need attributes
        // or if, it is variant, but we need variant attributes or variants them-self
        // TODO add this for attributes   if ((!isVariant && !noAttributes) || (isVariant && !noVariantAttributes && !noVariants)) {
        if ((!isVariant && !noAttributes) || (isVariant && !noVariantAttributes && !noVariants)) {
            // TODO large impact, REQUIRED FOR SWATCHE
            fields.push(this._getAttributesField(isVariant));
        }

        // to all products (non-variants)
        if (!isVariant) {
            fields.push(
                'url',
                this._getUrlRewritesFields()
            );

            if (!isPlp && !notRequireInfo && !isWidget) {
                fields.push(
                    this._getReviewCountField(),
                    this._getRatingSummaryField(),
                    this._getCustomizableProductFragment()
                );
            }

            if ((!noVariants && (isPlp || notRequireInfo || isWidget))) {
                // TODO small impact, REQUIRED FOR SWATCHE
                fields.push(
                    this._getConfigurableProductFragment()
                );
            } else if (!noVariants) {
                // if variants are not needed
                fields.push(
                    this._getConfigurableProductFragment(),
                    this._getBundleProductFragment(),
                    this._getGroupedProductItems()
                );
            }
        }

        // prevent linked products from looping
        if (isForLinkedProducts) {
            fields.push(this._getProductLinksField());
        }

        // additional information to PDP loads
        if (isSingleProduct) {
            fields.push(
                'stock_status',
                this._getDescriptionField(),
                this._getMediaGalleryField(),
                this._getSimpleProductFragment()
            );

            // for variants of PDP requested product
            if (!isVariant) {
                fields.push(
                    'canonical_url',
                    'meta_title',
                    'meta_keyword',
                    'meta_description',
                    new Field('variant_data')
                        .addFieldList([
                            'sku',
                            'name',
                            'image',
                            'currency',
                            'stock_status',
                            new Field('standard_properties')
                                .addFieldList([
                                    'name',
                                    'value'
                                ]),
                            new Field('additional_properties')
                                .addFieldList([
                                    'name',
                                    'value'
                                ])
                        ]),
                    this._getCategoriesField(),
                    this._getReviewsField(),
                    this._getVirtualProductFragment(),
                    this._getCustomizableProductFragment(),
                    this._getProductLinksField()
                );
            }
        }

        return fields;
    }

    /**
     * For grouped products, returns the subfields of the elements of the `items` field
     * @returns {*[]}
     * @private
     */
    _getGroupedProductItemFields() {
        return [
            this._getProductField(),
            'position',
            'qty'
        ];
    }

    /**
     * A GroupedProduct-specific field that queries the products that are grouped under this product
     * @returns {Field}
     * @private
     */
    _getGroupedProductItems() {
        return new Fragment('GroupedProduct').addField(
            new Field('items')
                .addFieldList(this._getGroupedProductItemFields())
        );
    }

    _getCartProductInterfaceFields() {
        return [
            'uid',
            'id',
            'sku',
            'name',
            'type_id',
            'stock_status',
            'brand_name',
            'url',
            'salable_qty',
            'original_price',
            'special_price',
            this._getStockItemField(),
            this._getProductThumbnailField(),
            this._getCartConfigurableProductFragment(),
            this._getAttributesField(false, true),
            this._getProductLinksField(),
            this.getPriceRangeField()
        ];
    }

    _getAttributeFields(isVariant = false, isCart = false) {
        return [
            'attribute_id',
            'attribute_value',
            'attribute_code',
            'attribute_label',
            'update_product_preview_image',
            ...this._getAdditionalAttributeFields(isCart),
            ...this._getAttributeOptionsFields(isVariant)
        ];
    }

    _getAttributesField(isVariant, isCart) {
        return new Field('s_attributes')
            .setAlias('attributes')
            .addFieldList(this._getAttributeFields(isVariant, isCart));
    }

    _getAggregationsFields() {
        const { args: { search } = {} } = this.options;
        const fileds = [
            new Field('label').setAlias('name'),
            new Field('attribute_code').setAlias('request_var'),
            'attribute_display_group',
            'is_boolean',
            'has_swatch',
            'position',
            'isSeo',
            'swatch_type',
            new Field('amshopby_filter_data')
                .addFieldList([
                    'visible_in_categories',
                    'categories_filter'
                ]),
            this._getAggregationsOptionsField()
        ];

        if (!search) {
            fileds.push(
                new Field('is_quick_filter')
            );
        }

        return fileds;
    }

    _getShopbySeoField() {
        return new Field('shopbyPage')
            .addFieldList(this._getShopbySeoFields());
    }

    _getShopbySeoFields() {
        return [
            'title',
            'description',
            'meta_title',
            'meta_description',
            'meta_keywords',
            'top_block',
            'bottom_block',
            'image',
            'canonical_url'
        ];
    }

    _getAggregationsOptionsField() {
        return new Field('options')
            .setAlias('filter_items')
            .addFieldList(this._getAggregationsOptionsFields());
    }

    _getAggregationsOptionsFields() {
        const { args: { search } = {} } = this.options;
        const fields = [
            'label',
            'count',
            'grouped_option_ids',
            'is_quick_filter',
            new Field('value').setAlias('value_string'),
            this._getSwatchDataField()
        ];

        if (!search) {
            fields.push(
                new Field('is_quick_filter'),
                new Field('quick_filter_position')
            );
        }

        return fields;
    }

    _getPageInfoField() {
        return new Field('page_info')
            .addField('current_page')
            .addField('total_pages');
    }

    _getArgumentsMap() {
        const { requireInfo } = this.options;
        const filterArgumentMap = this._getFilterArgumentMap();

        return {
            currentPage: { type: 'Int!' },
            pageSize: {
                type: 'Int!',
                handler: (option) => (requireInfo ? 1 : option)
            },
            search: {
                type: 'String!',
                handler: (option) => option.replace(/\+/g, ' ')
            },
            sort: {
                type: 'ProductAttributeSortInput!',
                handler: ({ sortKey, sortDirection }) => {
                    if (sortKey === NONE_SORT_OPTION_VALUE) {
                        return {};
                    }

                    if (sortKey === BEST_MATCH_SORT_OPTION_VALUE) {
                        return { [sortKey]: SORT_DIRECTION_TYPE.desc };
                    }

                    return { [sortKey]: sortDirection || SORT_DIRECTION_TYPE.asc };
                }
            },
            filter: {
                type: 'ProductAttributeFilterInput!',
                handler: (initialOptions = {}) => {
                    // add customer group by default to all requests
                    const { group_id } = BrowserDatabase.getItem(CUSTOMER) || {};

                    const options = {
                        ...initialOptions,
                        customerGroupId: group_id || '0'
                    };

                    const {
                        customFilters: { category_id } = {}
                    } = options;

                    /**
                     * Remove category ID from select, if there is a custom filter
                     * of category already selected in filtering options.
                     */
                    if (category_id) {
                         
                        options.categoryIds = undefined;
                    }

                    const parsedOptions = Object.entries(options).reduce(
                        (acc, [key, option]) => {
                            // if there is no value, or if the key is just not present in options object
                            if (!option || !filterArgumentMap[key]) {
                                return acc;
                            }

                            return { ...acc, ...filterArgumentMap[key](option) };
                        },
                        {}
                    );

                    return parsedOptions;
                }
            }
        };
    }/**
     * @returns {[string]} an array representing the subfields of the product thumbnail
     * @private
     */

    _getProductThumbnailFields() {
        return [
            'path',
            'url',
            'image_cover'
        ];
    }

    _getProductSmallFields() {
        return this._getProductThumbnailFields();
    }

    /**
     * Returns the field for fetching the thumbnail of a product.
     * Not to be confused with the media thumbnail field, which has the same name but is a subfield of media_gallery_entries
     * @returns {Field}
     * @private
     */
    _getProductThumbnailField() {
        return new Field('thumbnail')
            .addFieldList(this._getProductThumbnailFields());
    }

    _getProductSmallField() {
        return new Field('small_image')
            .addFieldList(this._getProductSmallFields());
    }

    _getProductImageField() {
        return new Field('image')
            .addFieldList(this._getProductThumbnailFields());
    }

    _getAttributeOptionField(noSwatches) {
        return [
            'label',
            'value',
            !noSwatches && this._getSwatchDataField(),
            'quality_symbol_image'
        ];
    }

    _getAttributeOptionsField(noSwatches) {
        return new Field('attribute_options')
            .addFieldList(this._getAttributeOptionField(noSwatches));
    }

    _getAdditionalAttributeFields(isCart) {
        if (isCart) {
            return [];
        }

        return [
            'attribute_type',
            'attribute_group_id',
            'attribute_group_name',
            'attribute_display_group',
            'attribute_display_position'
        ];
    }

    _getAttributeOptionsFields(isVariant) {
        if (isVariant) {
            return [];
        }

        return [
            this._getAttributeOptionsField()
        ];
    }

    _getMediaGalleryFields() {
        return [
            'id',
            'file',
            'label',
            'position',
            'disabled',
            'media_type',
            'types',
            'image_cover',
            this._getVideoContentField(),
            this._getMediaThumbnailField(),
            this._getMediaBaseField(),
            this._getMediaLargeField()
        ];
    }

    /**
     * Returns a field querying video-specific data for a media gallery entry.
     * @returns {Field} the video_content field
     * @private
     */
    _getVideoContentField() {
        return new Field('video_content').addFieldList([
            'media_type',
            'video_description',
            'video_metadata',
            'video_provider',
            'video_title',
            'video_url'
        ]);
    }

    /**
     * Returns a field querying the thumbnail of a media gallery entry.
     * Not to be confused with the product thumbnail field, which has the same name but is a direct subfield of the product
     * @returns {Field}
     * @private
     */
    // TODO add 'image_cover' once BE updates logic
    _getMediaThumbnailField() {
        return new Field('thumbnail').addFieldList(['url']);
    }

    _getMediaBaseField() {
        return new Field('base').addFieldList(['url']);
    }

    _getMediaLargeField() {
        return new Field('large').addFieldList(['url']);
    }

    _getMediaGalleryField() {
        return new Field('media_gallery_entries')
            .addFieldList(this._getMediaGalleryFields());
    }

    _getProductLinksField() {
        return new Field('product_links')
            .addFieldList(this._getProductLinkFields());
    }

    _getDescriptionFields() {
        return [
            'html'
        ];
    }

    _getDescriptionField() {
        return new Field('description')
            .addFieldList(this._getDescriptionFields());
    }

    _getQualitySymbolsOptionsFields() {
        return [
            'label',
            'selected',
            'quality_image',
            'quality_position',
            'show_on_usp',
            'quality_description'
        ];
    }

    _getQualitySymbolsOptionsField() {
        return new Field('options')
            .addFieldList(this._getQualitySymbolsOptionsFields());
    }

    _getQualitySymbolsFields() {
        return [
            'code',
            'label',
            'display_position',
            'display_description',
            this._getQualitySymbolsOptionsField()
        ];
    }

    _getQualitySymbolsField() {
        return new Field('quality_symbols')
            .addFieldList(this._getQualitySymbolsFields());
    }

    _getUrlRewritesFields() {
        return new Field('url_rewrites')
            .addFieldList(['url']);
    }

    _getProductLinkFields() {
        return [
            'position',
            'link_type',
            'linked_product_sku'
        ];
    }

    _getRatingsBreakdownFields() {
        return [
            new Field('name').setAlias('rating_code'),
            'value'
        ];
    }

    _getRatingsBreakdownField() {
        return new Field('ratings_breakdown')
            .setAlias('rating_votes')
            .addFieldList(this._getRatingsBreakdownFields());
    }

    _getReviewItemsFields() {
        return [
            'average_rating',
            'nickname',
            'created_at',
            'review_locale',
            new Field('summary').setAlias('title'),
            new Field('text').setAlias('detail'),
            new Field('original')
                .addFieldList([
                    new Field('summary').setAlias('title'),
                    new Field('text').setAlias('detail'),
                    new Field('review_locale').setAlias('review_locale'),
                    new Field('store_url').setAlias('store_url')
                ]),
            this._getRatingsBreakdownField()
        ];
    }

    _getReviewItemsField() {
        return new Field('items')
            .addFieldList(this._getReviewItemsFields());
    }

    _getReviewsFields() {
        return [
            this._getReviewItemsField()
        ];
    }

    _getReviewsField() {
        return new Field('reviews')
            // Hard-coded pages, it will be very hard to
            // paginate using current implementation
            // eslint-disable-next-line no-magic-numbers
            .addArgument('pageSize', 'Int', 20)
            .addArgument('currentPage', 'Int', 1)
            .addFieldList(this._getReviewsFields());
    }

    _getReviewCountField() {
        return new Field('review_count');
    }

    _getRatingSummaryField() {
        return new Field('rating_summary');
    }

    _getBundleOptionsFields() {
        return [
            'uid',
            'label',
            'quantity',
            'position',
            'is_default',
            'price',
            'price_type',
            'can_change_quantity',
            this._getProductBundleOptionField()
        ];
    }

    _getProductBundleOptionField() {
        return new Field('product')
            .addFieldList(this._getProductBundleOptionFields());
    }

    _getProductBundleOptionFields() {
        return [
            'name',
            'stock_status',
            this.getPriceRangeField()
        ];
    }

    _getBundleOptionsField() {
        return new Field('options')
            .addFieldList(this._getBundleOptionsFields());
    }

    _getBundleItemsFields() {
        return [
            'uid',
            'option_id',
            'title',
            'required',
            'type',
            'position',
            'sku',
            this._getBundleOptionsField()
        ];
    }

    _getBundleItemsField() {
        return new Field('items')
            .addFieldList(this._getBundleItemsFields());
    }

    _getBundlePriceOptionSelectionFields() {
        return [
            'selection_id',
            'final_option_price',
            'final_option_price_excl_tax',
            'regular_option_price',
            'regular_option_price_excl_tax'
        ];
    }

    _getBundlePriceOptionFields() {
        return [
            'option_id',
            new Field('selection_details')
                .addFieldList(this._getBundlePriceOptionSelectionFields())
        ];
    }

    _getBundlePriceOptionsField() {
        return new Field('bundle_options')
            .addFieldList(this._getBundlePriceOptionFields());
    }

    _getBundleProductFragmentFields() {
        return [
            'dynamic_price',
            'dynamic_sku',
            'ship_bundle_items',
            'dynamic_weight',
            this._getBundleItemsField(),
            this._getBundlePriceOptionsField()
        ];
    }

    _getValueFields() {
        const fields = [
            'value_index'
        ];

        return fields;
    }

    _getValuesField() {
        return new Field('values')
            .addFieldList(this._getValueFields());
    }

    _getConfigurableOptionFields(isCart) {
        const fields = [
            'attribute_code',
            this._getValuesField()
        ];

        if (!isCart) {
            fields.push('attribute_display_group', 'attribute_display_position');
        }

        return fields;
    }

    _getConfigurableOptionsField(isCart = false) {
        return new Field('configurable_options')
            .addFieldList(this._getConfigurableOptionFields(isCart));
    }

    _getVariantFields() {
        return [
            this._getProductField()
        ];
    }

    _getProductArguments() {
        const { args } = this.options;
        const argumentMap = this._getArgumentsMap();

        return Object.entries(args).reduce((acc, [key, arg]) => {
            if (!arg) {
                return acc;
            }
            const { type, handler = (option) => option } = argumentMap[key];

            return [...acc, [key, type, handler(arg)]];
        }, []);
    }

    _getCartConfigurableProductFragment() {
        return new Fragment('OptimizedConfigurableProduct')
            .addFieldList([
                this._getConfigurableOptionsField(true),
                this._getCartVariantsField()
            ]);
    }

    _getCartVariantsField() {
        return new Field('variants')
            .setAlias('variants')
            .addFieldList(this._getCartVariantFields());
    }

    _getCartVariantFields() {
        return [
            this._getCartProductField()
        ];
    }

    _getCartProductField() {
        return new Field('product')
            .addFieldList([
                'id',
                'sku',
                'stock_status',
                'salable_qty',
                this._getStockItemField(),
                this._getProductThumbnailField(),
                this._getAttributesField(true, true)
            ]);
    }

    getReviewsQuery(options, reviewsPageLimit, reviewsNextPage) {
        if (!options) {
            throw new Error('Missing argument `options`');
        }

        this.options = options;

        const products = new Field('products')
            .addFieldList(
                [
                    new Field('items').addFieldList(
                        [
                            this._getReviewsFieldPaginated(reviewsPageLimit, reviewsNextPage)
                        ]
                    )
                ]
            );

        this._getProductArguments().forEach((arg) => products.addArgument(...arg));

        return products;
    }

    _getReviewsFieldPaginated(reviewsPageLimit, reviewsNextPage) {
        return new Field('reviews')
            // Hard-coded pages, it will be very hard to
            // paginate using current implementation
             
            .addArgument('pageSize', 'Int', reviewsPageLimit)
            .addArgument('currentPage', 'Int', reviewsNextPage)
            .addFieldList(this._getReviewsFields());
    }
}

export default new ProductListQuery();
